import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { useCookieConsent, getAnalyticsConsent } from './useCookieConsent';

const FALLBACK_GA_MEASUREMENT_ID = 'G-BM1D8YTLGW';
const GA_MEASUREMENT_ID = import.meta.env.VITE_LOVABLE_CONNECTOR_GOOGLE_ANALYTICS_API_KEY || FALLBACK_GA_MEASUREMENT_ID;

let isGAInitialized = false;

export const initGA = () => {
  if (isGAInitialized) return;

  // Only initialize if user has given analytics consent
  if (!getAnalyticsConsent()) return;

  ReactGA.initialize(GA_MEASUREMENT_ID);
  isGAInitialized = true;
};

// Track GA4 events with custom parameters
export const trackEvent = (
  name: string,
  params?: Record<string, string | number | boolean | undefined>
) => {
  if (!getAnalyticsConsent() || !isGAInitialized) return;

  ReactGA.event(name, params);
};

// Predefined GA4 event trackers
export const trackFormSubmit = (formName: string, service: string, success: boolean) => {
  trackEvent('generate_lead', {
    form_name: formName,
    service,
    outcome: success ? 'success' : 'error',
  });
};

export const trackContactClick = (
  method: 'whatsapp' | 'phone' | 'email' | 'maps',
  value: string,
  location: string
) => {
  trackEvent('contact', {
    method,
    value,
    location,
  });
};

export const trackCTAClick = (label: string, location: string) => {
  trackEvent('cta_click', {
    label,
    location,
  });
};

export const trackLocationClick = (name: string, local: string) => {
  trackEvent('location_click', {
    name,
    local,
  });
};

export const useGoogleAnalytics = () => {
  const location = useLocation();
  const { hasAnalyticsConsent } = useCookieConsent();
  const previousPathRef = useRef<string | null>(null);

  // Official GA opt-out flag: stops all hits when consent is withdrawn
  useEffect(() => {
    (window as unknown as Record<string, boolean>)[`ga-disable-${GA_MEASUREMENT_ID}`] = !hasAnalyticsConsent;
  }, [hasAnalyticsConsent]);

  // Initialize GA when consent is given
  useEffect(() => {
    if (hasAnalyticsConsent && !isGAInitialized) {
      ReactGA.initialize(GA_MEASUREMENT_ID);
      isGAInitialized = true;

      // Send initial pageview after initialization
      ReactGA.send({
        hitType: 'pageview',
        page: location.pathname + location.search,
      });
      previousPathRef.current = location.pathname + location.search;
    }
  }, [hasAnalyticsConsent, location]);

  // Track page views only if consent given and GA initialized
  useEffect(() => {
    if (!hasAnalyticsConsent || !isGAInitialized) return;

    const currentPath = location.pathname + location.search;

    // Avoid duplicate pageviews
    if (previousPathRef.current === currentPath) return;

    ReactGA.send({
      hitType: 'pageview',
      page: currentPath,
    });

    previousPathRef.current = currentPath;
  }, [location, hasAnalyticsConsent]);
};

// Hook for components to use tracking functions with consent check
export const useTrackEvent = () => {
  const { hasAnalyticsConsent } = useCookieConsent();

  const track = useCallback((
    name: string,
    params?: Record<string, string | number | boolean | undefined>
  ) => {
    if (!hasAnalyticsConsent || !isGAInitialized) return;
    trackEvent(name, params);
  }, [hasAnalyticsConsent]);

  return { track, hasAnalyticsConsent };
};
